import { useRequiredContext } from "@redotech/react-util/context";
import {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";

/**
 * Open-options for the Ask Redo slide-out. Modelled as a
 * discriminated union so invalid combinations (auto-submit with no
 * prefill, prefill + threadId together, etc.) aren't representable.
 *
 *   - `"prefill"`: open a new conversation with the input prefilled,
 *     optionally auto-submitting.
 *   - `"thread"`: open an existing conversation by id.
 *   - `"directEscalation"`: open pre-escalated (e.g. recurring pickup
 *     flow that bypasses the agent and hands off to a human directly).
 *     Seeds the escalation context on mount.
 */
export type AskRedoPanelOptions =
  | { kind: "prefill"; input: string; autoSubmit?: boolean }
  | { kind: "thread"; threadId: string }
  | {
      kind: "directEscalation";
      escalationId: string;
      intercomConversationId: string;
      noticeTopic: string;
      triageThreadId: string;
    };

interface AskRedoSlidePanelContextValue {
  isOpen: boolean;
  panelOptions: AskRedoPanelOptions | null;
  open: (options?: AskRedoPanelOptions) => void;
  close: () => void;
  toggle: () => void;
  clearPanelOptions: () => void;
}

/**
 * Independent slide-panel state for the new Ask Redo chat.
 *
 * Deliberately NOT shared with `useAiSlidePanel` (the legacy panel):
 * the two systems are flag-gated mutually exclusive, and keeping
 * their state separate means we can rip out the legacy one cleanly
 * once we're 100% rolled out — no entangled refs to unwind.
 *
 * Lives in app-common (rather than the merchant app) so surfaces in
 * sibling packages — e.g. the customer portal builder in
 * merchant/general/app — can open the panel without a circular
 * package dependency on merchant/app.
 */
const AskRedoSlidePanelContext = createContext<
  AskRedoSlidePanelContextValue | undefined
>(undefined);

export function AskRedoSlidePanelProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [panelOptions, setPanelOptions] = useState<AskRedoPanelOptions | null>(
    null,
  );

  const open = useCallback((options?: AskRedoPanelOptions) => {
    if (options) {
      setPanelOptions(options);
    }
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setPanelOptions(null);
  }, []);

  // No `options` parameter: toggle is the no-context "flip" — callers
  // who want intent should call `open()` or `close()`. (The previous
  // shape silently dropped the options on the close branch.)
  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
    setPanelOptions(null);
  }, []);

  const clearPanelOptions = useCallback(() => setPanelOptions(null), []);

  // Memoise the value object so unrelated state changes don't force
  // every consumer to re-render. Callbacks are stable, so the only
  // identity change is when `isOpen` or `panelOptions` actually
  // changes.
  const value = useMemo(
    () => ({ isOpen, panelOptions, open, close, toggle, clearPanelOptions }),
    [isOpen, panelOptions, open, close, toggle, clearPanelOptions],
  );

  return (
    <AskRedoSlidePanelContext.Provider value={value}>
      {children}
    </AskRedoSlidePanelContext.Provider>
  );
}

export function useAskRedoSlidePanel(): AskRedoSlidePanelContextValue {
  return useRequiredContext(AskRedoSlidePanelContext);
}
